import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

export const ToastComponent: React.FC<ToastProps> = ({ toast, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        onClose(toast.id);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, onClose]);

  const typeClasses = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-teal-500 text-white',
    warning: 'bg-yellow-500 text-white',
  };

  const iconClasses = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠',
  };

  return (
    <div
      className={`
        ${typeClasses[toast.type]}
        px-4 py-3 rounded-lg shadow-lg mb-2 flex items-center justify-between gap-2
        w-[calc(100vw-2rem)] max-w-md min-w-0 animate-slide-in
      `}
    >
      <div className="flex min-w-0 items-center">
        <span className="mr-2 flex-shrink-0 text-lg">{iconClasses[toast.type]}</span>
        <span className="break-words">{toast.message}</span>
      </div>
      <button
        onClick={() => onClose(toast.id)}
        className="ml-1 flex-shrink-0 text-white hover:text-gray-200"
        aria-label={t('common.close')}
      >
        ✕
      </button>
    </div>
  );
};
